/**
 * Core Web Vitals monitoring and reporting to GA4
 * 
 * Tracks and reports Core Web Vitals metrics:
 * - LCP (Largest Contentful Paint)
 * - FID (First Input Delay) / INP (Interaction to Next Paint)
 * - CLS (Cumulative Layout Shift)
 */

import { getCLS, getFID, getLCP, getTTFB } from "web-vitals";
import type { Metric } from "web-vitals";
import { trackEvent } from "./analytics";

/**
 * Initialize Core Web Vitals monitoring
 * Call this once when the app loads
 */
export function initializeWebVitals(): void {
  // Largest Contentful Paint (LCP) - measures loading performance
  getLCP((metric: Metric) => {
    trackEvent("web_vital", {
      metric_name: "LCP",
      value: Math.round(metric.value),
      rating: (metric as any).rating || "unknown",
      delta: (metric as any).delta ? Math.round((metric as any).delta) : undefined,
    });
    
    // Log in development
    if (process.env.NODE_ENV === "development") {
      console.log("LCP:", metric);
    }
  });

  // First Input Delay (FID) - measures interactivity (deprecated but still available in v2)
  getFID((metric: Metric) => {
    trackEvent("web_vital", {
      metric_name: "FID",
      value: Math.round(metric.value),
      rating: (metric as any).rating || "unknown",
      delta: (metric as any).delta ? Math.round((metric as any).delta) : undefined,
    });
    
    if (process.env.NODE_ENV === "development") {
      console.log("FID:", metric);
    }
  });

  // Cumulative Layout Shift (CLS) - measures visual stability
  getCLS((metric: Metric) => {
    trackEvent("web_vital", {
      metric_name: "CLS",
      value: Math.round(metric.value * 1000) / 1000, // Keep 3 decimal places
      rating: (metric as any).rating || "unknown",
      delta: (metric as any).delta ? Math.round((metric as any).delta * 1000) / 1000 : undefined,
    });
    
    if (process.env.NODE_ENV === "development") {
      console.log("CLS:", metric);
    }
  });

  // Time to First Byte (TTFB) - measures server response time
  getTTFB((metric: Metric) => {
    trackEvent("web_vital", {
      metric_name: "TTFB",
      value: Math.round(metric.value),
      rating: (metric as any).rating || "unknown",
      delta: (metric as any).delta ? Math.round((metric as any).delta) : undefined,
    });
    
    if (process.env.NODE_ENV === "development") {
      console.log("TTFB:", metric);
    }
  });
}
